import { Component, OnInit } from '@angular/core';
import { PasswordModule } from "primeng/password";
import { Button } from "primeng/button";
import { Ripple } from "primeng/ripple";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import { AuthService } from "../../core/services/auth.service";
import { ToastService } from "../../core/services/toast.service";
import { StorageService } from "../../core/services/storage.service";
import {InputOtpModule} from "primeng/inputotp";
import {AuthResponse} from "../../core/models/auth.model";

@Component({
    selector: 'app-login',
    imports: [PasswordModule, ReactiveFormsModule, InputTextModule, Button, Ripple, InputOtpModule, RouterLink],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  login = new FormGroup({
    username: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    password: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
  });

  twoFactorForm = new FormGroup({
    code: new FormControl('', {nonNullable: true, validators: [Validators.required]})
  });

  showSpinner = false;
  isLoading: boolean = false;
  requiresTwoFactor: boolean = false;
  isTotpRequired: boolean = false;
  returnUrl: string = '';
  currentYear = new Date().getFullYear();

  constructor(private route: ActivatedRoute, private authService: AuthService, private tokenManager: StorageService,
              public toastService: ToastService, private router: Router) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.returnUrl = params['returnUrl'] || '/main';
    });
    this.authService.getPublicKey().subscribe({
      next: (response) => this.tokenManager.setPublicKey(response.publicKey),
      error: () => {}
    });
  }

  onSubmit() {
    this.isLoading = true;
    this.showSpinner = true;

    if(!this.login.invalid && this.login.value.username && this.login.value.password) {
      this.authService.signIn(this.login.value.username, this.login.value.password).subscribe({
        next: response => {
          if (response.requiresTwoFactor) {
            this.requiresTwoFactor = true;
            this.isTotpRequired = response.two_factor_type === 'TOTP';
            const message = response.two_factor_type === 'TOTP'
              ? 'Veuillez saisir le code depuis votre application d\'authentification.'
              : 'Un code vous a été envoyé par email. Veuillez le saisir ci-dessous.';
            this.toastService.showToast('Authentification à double facteur', message);
          } else this.handleSuccessfulLogin(response);
          this.isLoading = false;
          this.showSpinner = false;
        },
        error: (err) => {
          this.toastService.showToast('Erreur lors de l\'authentification', err.error, 'error');
          this.isLoading = false;
          this.showSpinner = false;
        }
      });
    }
  }

  onSubmitTwoFactor() {
    this.isLoading = true;
    this.showSpinner = true;

    if (!this.twoFactorForm.invalid && this.twoFactorForm.value.code) {
      const username = this.login.value.username!;
      const code = this.twoFactorForm.value.code;

      if (this.isTotpRequired) {
        // Utiliser l'endpoint TOTP avec le code comme nombre
        this.authService.verifyTotp(username, Number(code)).subscribe({
          next: response => {
            this.handleSuccessfulLogin(response);
            this.isLoading = false;
            this.showSpinner = false;
          },
          error: (err) => {
            this.toastService.showToast('Code TOTP invalide', err.error, 'error');
            this.isLoading = false;
            this.showSpinner = false;
          }
        });
      } else {
        // Utiliser l'endpoint 2FA email avec le code comme string
        this.authService.verifyTwoFactor(username, code).subscribe({
          next: response => {
            this.handleSuccessfulLogin(response);
            this.isLoading = false;
            this.showSpinner = false;
          },
          error: (err) => {
            this.toastService.showToast('Token invalide', err.error, 'error');
            this.isLoading = false;
            this.showSpinner = false;
          }
        });
      }
    }
  }

  private handleSuccessfulLogin(response: AuthResponse) {
    this.toastService.showToast('Connexion réussie', 'Vous êtes maintenant connecté. Bienvenue !');
    this.tokenManager.setToken(response.access_token);
    this.tokenManager.setRefreshToken(response.refresh_token);
    this.router.navigate([this.returnUrl]).then();
  }
}
